import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { FlamingoAuthService } from '@flamingo/service/flamingo-auth.service';
import { FlamingoMessageService } from '@flamingo/service/flamingo-message.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseConfigService } from '@fuse/services/config.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { navigation } from 'app/navigation/navigation';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MainService } from '../../../children/main/main.service';
import { UserType } from '../../../core/model/user.model';
import { locale as english } from '../../i18n/en';
import { locale as italian } from '../../i18n/it';
import { LemonwayOnboardingService } from '../../../component/profile/services/lemonway-onboarding.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SellerAccountView } from '../../../core/model/seller-account-view.model';
import { OnboardingErrorsDialogComponent } from '../../../component/profile/dialogs/onboarding-errors-dialog.component';
import { Input } from '@angular/core';
import { BuyerAccountView } from '../../../core/model/buyer-account-view.model';
import { OnboardingAlertDialogComponent } from '../../../component/onboarding-alert-dialog/onboarding-alert-dialog.component';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
@Component({
  selector: 'toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ToolbarComponent implements OnInit, OnDestroy {

  @Input() userProfile: SellerAccountView | BuyerAccountView;

  horizontalNavbar: boolean;
  rightNavbar: boolean;
  hiddenNavbar: boolean;
  languages: any;
  navigation: any;
  selectedLanguage: any;
  userStatusOptions: any[];

  private _unsubscribeAll: Subject<any>;

  constructor(
    public mainService: MainService,
    private _authService: FlamingoAuthService,
    private _fuseConfigService: FuseConfigService,
    private _fuseSidebarService: FuseSidebarService,
    private _messageService: FlamingoMessageService,
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private lemonwayService: LemonwayOnboardingService,
    private _router: Router,
    private _translationLoader: FuseTranslationLoaderService,
    private _translateService: TranslateService,
  ) {
    this._translationLoader.loadTranslations(italian, english);

    // Set the defaults
    this.userStatusOptions = [
      {
        title: 'Online',
        icon: 'icon-checkbox-marked-circle',
        color: '#4CAF50',
      },
      {
        title: 'Away',
        icon: 'icon-clock',
        color: '#FFC107',
      },
      {
        title: 'Do not Disturb',
        icon: 'icon-minus-circle',
        color: '#F44336',
      },
      {
        title: 'Invisible',
        icon: 'icon-checkbox-blank-circle-outline',
        color: '#BDBDBD',
      },
      {
        title: 'Offline',
        icon: 'icon-checkbox-blank-circle-outline',
        color: '#616161',
      },
    ];

    this.languages = [
      {
        id: 'it',
        title: 'Italian',
        flag: 'it',
      },
      {
        id: 'en',
        title: 'English',
        flag: 'gb',
      },
    ];

    this.navigation = navigation;

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  get hasOnboardingAccount(): boolean {
    const user = this.mainService.user as SellerAccountView;
    return user?.showOnboardingButton === 2;
  }

  get canDoOnboarding(): boolean {
    const user = this.mainService.user as SellerAccountView;
    const showButton = user?.showOnboardingButton;
    return showButton === 1 || showButton === 2;
  }

  get showOnboardingButton(): boolean {
    return this.canDoOnboarding;
  }

  get isBuyer(): boolean {
    return this.mainService.user?.profileType === 'BUYER';
  }

  get canDoBuyerOnboarding(): boolean {
    const user = this.mainService.user as BuyerAccountView;
    const showButton = user?.showOnboardingButton;
    return this.isBuyer && (showButton === 1 || showButton === 2);
  }

  get hasBuyerOnboardingAccount(): boolean {
    const user = this.mainService.user as BuyerAccountView;
    return user?.showOnboardingButton === 2;
  }

  ngOnInit(): void {
    // Subscribe to the config changes
    this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(settings => {
      this.horizontalNavbar = settings.layout.navbar.position === 'top';
      this.rightNavbar = settings.layout.navbar.position === 'right';
      this.hiddenNavbar = settings.layout.navbar.hidden === true;
    });

    // Set the selected language from default languages
    this.selectedLanguage = _.find(this.languages, { id: this._translateService.currentLang });
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  toggleSidebarOpen(key): void {
    this._fuseSidebarService.getSidebar(key).toggleOpen();
  }

  showShortcuts(): boolean {
    if (!!this.mainService.user && !!this._authService.user) {
      return (
        !!this.mainService.user.companyStatus &&
        this.mainService.user.companyStatus !== 'PENDING_VALIDATION' &&
        this.mainService.user.companyStatus !== 'PROFILE_CONFIRM' &&
        this.mainService.user.companyStatus !== 'LEAD' &&
        this.mainService.user.companyStatus !== 'DANGEROUS'
      );
    }
    return false;
  }

  logout(): void {
    this._messageService
      .showConfirmDialog(
        this._translateService.instant('TOOLBAR.LOGOUT.CONFIRM'),
        this._translateService.instant('TOOLBAR.LOGOUT.MESSAGE'),
        this._translateService.instant('TOOLBAR.LOGOUT.CANCEL'),
        this._translateService.instant('TOOLBAR.LOGOUT.EXIT'),
      )
      .subscribe(res => {
        if (res) {
          const profileType = this._authService.user.type ? this._authService.user.type : null;
          this._authService.logout();
          switch (profileType) {
            case UserType.seller:
              this._router.navigate(['/auth']);
              break;
            case UserType.buyer:
              this._router.navigate(['/buyer/auth']);
              break;
            case UserType.partner:
            case UserType.partnerBranchMaster:
            case UserType.partnerAgent:
              this._router.navigate(['/partner/auth']);
              break;
            default:
              this._router.navigate(['/auth']);
              break;
          }
        }
      });
  }

  setLanguage(lang): void {
    // Set the selected language for the toolbar
    this.selectedLanguage = lang;

    // Use the selected language for translations
    this._translateService.use(lang.id);

    // Set language to local session
    localStorage.setItem('lang', lang.id);
  }

  startOnboarding(): void {
    const user = this.mainService.user as SellerAccountView;
    const sellerId = user?.sellerId;
    if (!sellerId) return;

    const hasAccountId = !!user.onboardingAccountId;
    const hasProfileId = !!user.onboardingId;

    if (!hasAccountId && !hasProfileId) {
      // create, then initiate, then resume
      this.lemonwayService.createAccount(sellerId).pipe(
        switchMap(errors => {
          if (errors && errors.length > 0) {
            this._dialog.open(OnboardingErrorsDialogComponent, {
              data: { errors, isSeller: true },
              width: '500px'
            });
            return of(null);
          }
          return this.lemonwayService.initiateOnboarding(sellerId);
        }),
        switchMap(initiateErrors => {
          if (!initiateErrors) return of(null);
          if (initiateErrors.length > 0) {
            this._dialog.open(OnboardingErrorsDialogComponent, {
              data: { errors: initiateErrors, isSeller: true  },
              width: '500px'
            });
            return of(null);
          }
          return this.lemonwayService.getResumeUrl(sellerId);
        })
      ).subscribe((url: any) => {
        if (url && url.url) {
          const newWindow = window.open(url.url, '_blank');
          if (!newWindow) {
            this._snackBar.open('Il browser ha bloccato l\'apertura della nuova scheda. Per favore, controlla le impostazioni.', 'Chiudi', { duration: 5000 });
          }
        }
      });
    } else if (hasAccountId && !hasProfileId) {
      // initiate, then resume
      this.lemonwayService.initiateOnboarding(sellerId).pipe(
        switchMap(initiateErrors => {
          if (initiateErrors && initiateErrors.length > 0) {
            this._dialog.open(OnboardingErrorsDialogComponent, {
              data: { errors: initiateErrors, isSeller: true  },
              width: '500px'
            });
            return of(null);
          }
          return this.lemonwayService.getResumeUrl(sellerId);
        })
      ).subscribe((url: any) => {
        if (url && url.url) {
          const newWindow = window.open(url.url, '_blank');
          if (!newWindow) {
            this._snackBar.open('Il browser ha bloccato l\'apertura della nuova scheda. Per favore, controlla le impostazioni.', 'Chiudi', { duration: 5000 });
          }
        }
      });
    } else if (hasAccountId && hasProfileId) {
      // solo resume
      this.lemonwayService.getResumeUrl(sellerId).subscribe((url: any) => {
        if (url && url.url) {
          const newWindow = window.open(url.url, '_blank');
          if (!newWindow) {
            this._snackBar.open('Il browser ha bloccato l\'apertura della nuova scheda. Per favore, controlla le impostazioni.', 'Chiudi', { duration: 5000 });
          }
        }
      });
    }
  }

  resumeOnboarding(): void {
    const user = this.mainService.user as SellerAccountView;
    const sellerId = user?.sellerId;
    if (!sellerId) return;
    const hasAccountId = !!user.onboardingAccountId;
    const hasProfileId = !!user.onboardingId;
    if (hasAccountId && !hasProfileId) {
      // initiate, then resume
      this.lemonwayService.initiateOnboarding(sellerId).pipe(
        switchMap(initiateErrors => {
          if (initiateErrors && initiateErrors.length > 0) {
            this._dialog.open(OnboardingErrorsDialogComponent, {
              data: { errors: initiateErrors, isSeller: true  },
              width: '500px'
            });
            return of(null);
          }
          return this.lemonwayService.getResumeUrl(sellerId);
        })
      ).subscribe((url: any) => {
        if (url && url.url) {
          const newWindow = window.open(url.url, '_blank');
          if (!newWindow) {
            this._snackBar.open('Il browser ha bloccato l\'apertura della nuova scheda. Per favore, controlla le impostazioni.', 'Chiudi', { duration: 5000 });
          }
        }
      });
    } else if (hasAccountId && hasProfileId) {
      // solo resume
      this.lemonwayService.getResumeUrl(sellerId).subscribe((url: any) => {
        if (url && url.url) {
          const newWindow = window.open(url.url, '_blank');
          if (!newWindow) {
            this._snackBar.open('Il browser ha bloccato l\'apertura della nuova scheda. Per favore, controlla le impostazioni.', 'Chiudi', { duration: 5000 });
          }
        }
      });
    }
  }

  startBuyerOnboarding(): void {
    const buyerId = (this.mainService.user as BuyerAccountView)?.buyerId;
    if (buyerId) {
      this.lemonwayService.startBuyerOnboarding(buyerId).subscribe({
        next: (response) => {
          if (response.errors) {
            this._dialog.open(OnboardingErrorsDialogComponent, {
              data: { errors: response.errors, isSeller: false },
              width: '500px'
            });
          } else {
            this.resumeBuyerOnboarding();
          }
        },
        error: (error) => {
          this._snackBar.open(error?.message || 'Errore durante l\'avvio dell\'onboarding', 'Chiudi', { duration: 5000 });
        }
      });
    }
  }

  resumeBuyerOnboarding(): void {
    const buyerId = (this.mainService.user as BuyerAccountView)?.buyerId;
    if (buyerId) {
      this.lemonwayService.resumeBuyerOnboarding(buyerId).subscribe({
        next: (url) => {
          if (url) {
            const newWindow = window.open(url, '_blank');
            if (!newWindow) {
              this._snackBar.open('Il browser ha bloccato l\'apertura della nuova scheda.', 'Chiudi', { duration: 5000 });
            }
          } else {
            this._snackBar.open('URL non valido per l\'onboarding', 'Chiudi', { duration: 3000 });
          }
        },
        error: () => {
          this._snackBar.open('Errore durante la ripresa dell\'onboarding', 'Chiudi', { duration: 3000 });
        }
      });
    }
  }
}
